import { useRef, useEffect, useState } from 'react';
import { GripVertical, ChevronDown, ChevronRight } from 'lucide-react';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';

/**
 * Wrapper component for draggable sections in edit mode
 * When not in edit mode, renders collapsible section with header
 * When in edit mode, shows a compact row with drag handle and expanded toggle
 */
export default function DraggableSection({
  section,
  sectionConfig,
  editMode,
  onToggleExpanded,
  onDragStart,
  onDrop,
  children
}) {
  const ref = useRef(null);
  const handleRef = useRef(null);
  const [localDragging, setLocalDragging] = useState(false);
  const [localDropTarget, setLocalDropTarget] = useState(false);
  // Track current expanded state (initialized from defaultExpanded)
  const [isExpanded, setIsExpanded] = useState(section.defaultExpanded);

  // Reset expanded state when defaultExpanded changes (e.g., after saving preferences)
  useEffect(() => {
    setIsExpanded(section.defaultExpanded);
  }, [section.defaultExpanded]);

  useEffect(() => {
    if (!editMode || !ref.current) return;

    const element = ref.current;
    const handle = handleRef.current;

    return combine(
      draggable({
        element,
        dragHandle: handle,
        getInitialData: () => ({ sectionId: section.id, index: section.index }),
        onDragStart: () => {
          setLocalDragging(true);
          onDragStart?.(section.id);
        },
        onDrop: () => {
          setLocalDragging(false);
        },
      }),
      dropTargetForElements({
        element,
        getData: () => ({ sectionId: section.id, index: section.index }),
        onDragEnter: () => setLocalDropTarget(true),
        onDragLeave: () => setLocalDropTarget(false),
        onDrop: ({ source }) => {
          setLocalDropTarget(false);
          if (source.data.sectionId !== section.id) {
            onDrop?.(source.data.sectionId, section.id);
          }
        },
      })
    );
  }, [editMode, section.id, section.index, onDragStart, onDrop]);

  // Normal mode - render collapsible section
  if (!editMode) {
    const Icon = sectionConfig?.icon;

    return (
      <div className="space-y-3">
        {/* Collapsible header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center gap-2 text-left group"
        >
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-300 transition-colors" />
          ) : (
            <ChevronRight className="w-5 h-5 text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-300 transition-colors" />
          )}
          {Icon && <Icon className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />}
          <span className="font-medium text-neutral-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
            {sectionConfig?.title || section.id}
          </span>
        </button>

        {/* Collapsible content */}
        {isExpanded && children}
      </div>
    );
  }

  // Edit mode - show compact draggable row
  const Icon = sectionConfig?.icon;

  return (
    <div
      ref={ref}
      className={`
        rounded-lg border transition-all duration-200
        ${localDragging ? 'opacity-50 border-primary-400 bg-primary-50 dark:bg-primary-900/30' : ''}
        ${localDropTarget && !localDragging ? 'border-primary-500 border-2 bg-primary-50 dark:bg-primary-900/30' : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800'}
      `}
    >
      <div className="flex items-center gap-3 p-3">
        {/* Drag handle */}
        <button
          ref={handleRef}
          type="button"
          className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 cursor-grab active:cursor-grabbing"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-5 h-5" />
        </button>

        {/* Section icon */}
        {Icon && <Icon className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />}

        {/* Section title */}
        <span className="font-medium text-neutral-900 dark:text-white flex-1">
          {sectionConfig?.title || section.id}
        </span>

        {/* Expanded by default toggle */}
        <label className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300 cursor-pointer">
          <input
            type="checkbox"
            checked={section.defaultExpanded}
            onChange={() => onToggleExpanded(section.id)}
            className="w-4 h-4 rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 dark:bg-neutral-700"
          />
          <span className="hidden sm:inline">Expanded by default</span>
          <span className="sm:hidden">Expanded</span>
        </label>
      </div>
    </div>
  );
}
